import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import UtenteMaster from "../../../interfaces/utenteMaster";
import { useState, useEffect } from "react";
import createUtenteMaster from "../../../api/utenteMaster/createUtenteMaster";
import updateUtenteMaster from "../../../api/utenteMaster/updateUtenteMaster";
import { BluAlert, BluLoader } from "@bludata/components";
import Gruppo from "../../../interfaces/gruppo";
import isValidEmail from "../../../lib/isValidEmail";
import setInviaResetPassword from "../../../api/utenteMaster/setInviaResetPassword";

interface Props {
  onClose: () => void;
  utente: UtenteMaster;
  gruppoList: Gruppo[];
  isEdit?: boolean;
}

function DialogModificaUtente(props: Props) {
  const { onClose, utente, gruppoList, isEdit = false } = props;
  const [nomeUtente, setNomeUtente] = useState(utente.nomeUtente || "");
  const [user, setUser] = useState(utente.user || "");
  const [eMail, setEMail] = useState(utente.eMail || "");
  const [gruppo, setGruppo] = useState(utente.gruppo || "");
  const [descrizioneGruppo, setDescrizioneGruppo] = useState(
    utente.descrizioneGruppo || ""
  );
  const [attivo, setAttivo] = useState(utente.attivo || false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showResetPasswordAlert, setShowResetPasswordAlert] = useState(false);

  const validForm =
    nomeUtente && user && gruppo && eMail && isValidEmail(eMail);

  const onSaveClick = () => {
    if (!validForm) {
      setError("Tutti i campi sono obbligatori e l'email deve essere valida.");
      return;
    }

    const updatedUtente = {
      ...utente,
      nomeUtente,
      user,
      eMail,
      gruppo,
      descrizioneGruppo,
      attivo,
    };

    setLoading(true);

    // If editing existing user, use updateUtenteMaster, otherwise createUtenteMaster
    const savePromise = isEdit
      ? updateUtenteMaster(utente.id!, updatedUtente)
      : createUtenteMaster({ ...updatedUtente, id: undefined });

    savePromise
      .then(() => {
        onClose();
      })
      .catch((e) => {
        setError(
          e ||
            `Si è verificato un errore durante la ${
              isEdit ? "modifica" : "creazione"
            } dell'utente.`
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResetPasswordConfirm = () => {
    setInviaResetPassword(eMail, user)
      .then(() => {
        setShowResetPasswordAlert(false);
        setError("");
      })
      .catch((err) => {
        setError(
          err?.response?.data?.response ||
            "Errore nell'invio della mail di reset password"
        );
      })
      .finally(() => {
        setShowResetPasswordAlert(false);
      });
  };

  useEffect(() => {
    if (gruppo) {
      const selectedGroup = gruppoList.find((g) => g.codiceGruppo === gruppo);
      if (selectedGroup) {
        setDescrizioneGruppo(selectedGroup.descrizione);
      }
    }
  }, [gruppo, gruppoList]);

  return (
    <Dialog open fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? "Modifica utente" : "Crea un nuovo utente"}
      </DialogTitle>
      <DialogContent>
        <BluLoader open={loading} />
        <BluAlert
          show={!!error}
          handleClose={() => {
            setError("");
          }}
          text={error}
          type="error"
        />
        <BluAlert
          show={showResetPasswordAlert}
          handleClose={() => {
            setShowResetPasswordAlert(false);
          }}
          handleConfirm={handleResetPasswordConfirm}
          text={
            "Stai per inviare una mail al cliente per resettare la password. Sei sicuro di voler procedere?"
          }
          title="Attenzione"
          type="warning"
        />
        <Stack flexDirection="column" spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Nome Utente"
            value={nomeUtente}
            onChange={(e) => setNomeUtente(e.target.value)}
            fullWidth
          />
          <TextField
            label="User"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            fullWidth
          />
          <TextField
            label="Email"
            value={eMail}
            onChange={(e) => setEMail(e.target.value)}
            error={eMail.length > 0 && !isValidEmail(eMail)}
            helperText={
              eMail.length > 0 && !isValidEmail(eMail) ? "Email non valida" : ""
            }
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Gruppo</InputLabel>
            <Select
              value={gruppo}
              label="Gruppo"
              onChange={(e) => setGruppo(e.target.value)}
            >
              {gruppoList.map((g) => (
                <MenuItem key={g.codiceGruppo} value={g.codiceGruppo}>
                  {g.descrizione}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={attivo}
                onChange={(e) => setAttivo(e.target.checked)}
              />
            }
            label="Attivo"
          />
          {isEdit && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowResetPasswordAlert(true)}
            >
              Invia reset password
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Annulla
        </Button>
        <Button variant="contained" onClick={onSaveClick} disabled={!validForm}>
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DialogModificaUtente;
